/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient from '../../services/apiService';
import {
  AggregationDefinition,
  AggregationFunction,
  AggregationTarget,
  AggregationType,
  AiReport,
  AiReportEntry,
  Anomaly,
  CleanRecipe,
  CleanStep,
  ColCardinalityHint,
  ColDataType,
  ColMeasurementLevel,
  CollectionTemplate,
  CollectionType,
  ColumnDataType,
  ColumnMapping,
  DataSet,
  DataSource,
  DataSourceLink,
  DataSourceLinkCreate,
  SourceRule,
  SourceRuleCreate,
  SourceRuleUpdate,
  MonitoringSchedule,
  PipelineDependencyStatus,
  RiskOption,
  Rule,
  RuleBoardRow,
  RuleJoinKey,
  RuleRun,
  RuleSource,
  RuleStep,
  RunCleanResult,
  RunWithOutput,
  ScheduleMode,
  StandardColumn,
  UploadMode,
} from './types';

const BASE = '/api/v1/fan';

// ─────────────────────────────────────────────────────────────────────────
// Adapters — backend rows → frontend domain types
// ─────────────────────────────────────────────────────────────────────────

const toDataSource = (r: any): DataSource => ({
  id: String(r.id),
  name: r.name,
  description: r.description ?? '',
  entity_id: String(r.entity_id ?? ''),
  created_by: String(r.created_by ?? ''),
  created_at: r.created_on ?? '',
  dataset_count: Number(r.dataset_count ?? 0),
  total_rows: Number(r.total_rows ?? 0),
  collection_type_id: r.collection_type_id != null ? String(r.collection_type_id) : undefined,
  standard_schema: Array.isArray(r.standard_schema) ? r.standard_schema.map((c: any): StandardColumn => {
    // New 3-field shape from DB
    if (c.data_type) {
      return {
        name:              c.name ?? '',
        display_name:      c.display_name ?? c.name ?? '',
        data_type:         c.data_type as ColDataType,
        measurement_level: (c.measurement_level ?? 'nominal') as ColMeasurementLevel,
        cardinality_hint:  (c.cardinality_hint ?? 'high_cardinality') as ColCardinalityHint,
        required:          !!c.required,
        description:       c.description,
      };
    }
    // Legacy shape: map semantic_type → 3 fields
    const st: string = c.semantic_type ?? c.type ?? '';
    let dt: ColDataType = 'text';
    let ml: ColMeasurementLevel = 'nominal';
    let ch: ColCardinalityHint = 'high_cardinality';
    if (st === 'numeric' || st === 'continuous' || st === 'number') { dt = 'decimal'; ml = 'ratio'; ch = 'continuous'; }
    else if (st === 'categorical') { dt = 'text'; ml = 'nominal'; ch = 'low_cardinality'; }
    else if (st === 'date' || st === 'datetime') { dt = st as ColDataType; ml = 'interval'; ch = 'high_cardinality'; }
    else if (st === 'percent') { dt = 'percentage'; ml = 'ratio'; ch = 'continuous'; }
    return {
      name: c.name ?? '', display_name: c.display_name ?? c.name ?? '',
      data_type: dt, measurement_level: ml, cardinality_hint: ch,
      required: !!c.required, description: c.description,
    };
  }) : [],
  allowed_source_types: Array.isArray(r.allowed_source_types) ? r.allowed_source_types : ['csv', 'xlsx', 'xls', 'txt'],
  icon_key: r.icon_key ?? 'custom',
  color_key: r.color_key ?? 'indigo',
  // ── Phase-114 snapshot fields ─────────────────────────────────────────
  source_type: (r.source_type ?? 'normal') as 'normal' | 'combined' | 'api',
  saved_parquet_key: r.saved_parquet_key ?? null,
  saved_at: r.saved_at ?? null,
  saved_row_count: r.saved_row_count != null ? Number(r.saved_row_count) : null,
  api_endpoint: r.api_endpoint ?? null,
  collection_mode: r.collection_mode ?? null,
  api_params: Array.isArray(r.api_params) ? r.api_params : null,
  api_dynamic_param_keys: Array.isArray(r.api_dynamic_param_keys) ? r.api_dynamic_param_keys : null,
  api_auth_type: r.api_auth_type ?? null,
  api_login_url: r.api_login_url ?? null,
  api_token_path: r.api_token_path ?? null,
  api_login_user_field: r.api_login_user_field ?? null,
  api_login_pwd_field: r.api_login_pwd_field ?? null,
  // api_credentials_enc is NEVER mapped — excluded from API response by backend (BRD §8, T-114-API)
  authenticator_id: r.authenticator_id ?? null,
  authenticator_name: r.authenticator_name ?? null,
  authenticator_session_valid: r.authenticator_session_valid ?? null,
});

const toDataSet = (r: any): DataSet => ({
  id: String(r.id),
  data_source_id: String(r.data_source_id),
  file_name: r.file_name,
  format: r.format,
  s3_key: r.s3_raw_key ?? r.s3_parquet_key ?? '',
  row_count: Number(r.row_count ?? 0),
  size_bytes: Number(r.size_bytes ?? 0),
  uploaded_by: String(r.created_by ?? ''),
  uploaded_at: r.created_on ?? '',
  version: Number(r.version ?? 1),
  status: (r.status ?? 'Ready') as DataSet['status'],
  kind: (r.kind ?? 'raw') as DataSet['kind'],
  upload_mode: (r.upload_mode ?? 'new') as UploadMode,
  parent_dataset_id: r.parent_dataset_id != null ? String(r.parent_dataset_id) : undefined,
  sheet_name: r.sheet_name ?? undefined,
  // Reversible descope flag (Plan 109-01). Backend defaults TRUE; if the row predates
  // migration 174 (or the field is absent) treat the batch as in-scope so it still runs.
  in_scope: r.in_scope == null ? true : !!r.in_scope,
  columns: Array.isArray(r.columns)
    ? r.columns.map((c: any) => ({
        name: c.name,
        data_type: (c.semantic_type ?? 'text') as ColumnDataType,
        null_count: Number(c.null_count ?? 0),
        distinct_count: c.distinct_count != null ? Number(c.distinct_count) : undefined,
        sample_values: c.sample_values
          ? String(c.sample_values).split(',').map((s: string) => s.trim()).filter(Boolean)
          : undefined,
      }))
    : [],
});

const toCleanStep = (s: any, idx: number): CleanStep => ({
  id: s.id != null ? String(s.id) : undefined,
  step_number: Number(s.step_number ?? idx + 1),
  operation: s.operation as CleanStep['operation'],
  params: (typeof s.params === 'object' && s.params !== null) ? s.params : {},
  is_active: s.is_active != null ? Number(s.is_active) : 1,
});

const toCleanRecipe = (r: any): CleanRecipe => ({
  id: String(r.id),
  pipeline_id: r.pipeline_id != null ? String(r.pipeline_id) : undefined,
  data_source_id: r.data_source_id != null ? String(r.data_source_id) : undefined,
  name: r.name ?? 'Default Recipe',
  is_active: Number(r.is_active ?? 1),
  created_on: r.created_on ?? '',
  steps: Array.isArray(r.steps) ? r.steps.map(toCleanStep) : [],
});

const toAiReport = (r: any): AiReport => ({
  id: String(r.id),
  dataset_id: String(r.dataset_id),
  generated_at: r.generated_at ?? '',
  report_json: (typeof r.report_json === 'object' && r.report_json !== null)
    ? r.report_json as Record<string, AiReportEntry>
    : {},
});

const toAggregation = (r: any): AggregationDefinition => {
  const spec = typeof r.spec === 'string' ? safeParse(r.spec) : (r.spec ?? {});
  return {
    id: String(r.id),
    name: r.name,
    source_dataset_id: String(r.source_dataset_id),
    source_dataset_name: r.source_dataset_name ?? '',
    data_source_id: r.data_source_id ?? null,
    type: r.type as AggregationType,
    group_by_columns: spec.group_by_columns ?? [],
    measure_column: spec.measure_column,
    measure_aggregation: spec.measure_aggregation,
    measure_aggregations: Array.isArray(spec.measure_aggregations)
      ? (spec.measure_aggregations as AggregationFunction[])
      : spec.measure_aggregation
        ? [spec.measure_aggregation as AggregationFunction]
        : undefined,
    pivot_column: spec.pivot_column,
    window_size: spec.window_size,
    filter_expression: spec.filter_expression,
    output_dataset_id: r.output_dataset_id != null ? String(r.output_dataset_id) : undefined,
    sources: Array.isArray(r.sources) ? r.sources.map(toRuleSource) : [],
    created_by: String(r.created_by ?? ''),
    created_at: r.created_on ?? '',
    last_run_at: r.last_run_at,
    status: (r.status ?? 'Draft') as AggregationDefinition['status'],
  };
};

const toRuleSource = (src: any): RuleSource => {
  let joinKeys: RuleJoinKey[] = [];
  if (typeof src.join_keys === 'string') {
    try { joinKeys = JSON.parse(src.join_keys); } catch { joinKeys = []; }
  } else if (Array.isArray(src.join_keys)) {
    joinKeys = src.join_keys as RuleJoinKey[];
  }
  return {
    alias: src.alias ?? '',
    data_source_id: Number(src.data_source_id),
    data_source_name: src.data_source_name ?? undefined,
    role: (src.role ?? 'primary') as RuleSource['role'],
    join_type: src.join_type ?? null,
    join_keys: joinKeys,
    sort_order: Number(src.sort_order ?? 0),
  };
};

const toRule = (r: any): Rule => ({
  id: String(r.id),
  name: r.name,
  description: r.description ?? '',
  entity_id: String(r.entity_id ?? ''),
  severity: (r.severity ?? 'Medium') as Rule['severity'],
  tags: Array.isArray(r.tags)
    ? r.tags
    : typeof r.tags === 'string'
      ? r.tags.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [],
  created_by: String(r.created_by ?? ''),
  created_at: r.created_on ?? '',
  last_run_at: r.last_run_at,
  status: (r.status ?? 'Draft') as Rule['status'],
  last_output_dataset_id: r.last_output_dataset_id != null ? String(r.last_output_dataset_id) : undefined,
  last_rows_matched: r.last_rows_matched != null ? Number(r.last_rows_matched) : undefined,
  last_rows_in: r.last_rows_in != null ? Number(r.last_rows_in) : undefined,
  collection_type_id: r.collection_type_id ?? null,
  data_source_id: r.data_source_id ?? null,
  agg_target_dataset_id: r.agg_target_dataset_id ?? null,
  risk_id: r.risk_id ?? null,
  risk_code: r.risk_code ?? undefined,
  risk_name: r.risk_name ?? undefined,
  sources: Array.isArray(r.sources) ? r.sources.map(toRuleSource) : [],
  steps: Array.isArray(r.steps)
    ? r.steps.map(
        (s: any, idx: number): RuleStep => ({
          id: String(s.id),
          step_number: Number(s.step_number ?? idx + 1),
          target_dataset_id: String(s.target_dataset_id),
          target_dataset_name: s.target_dataset_name ?? '',
          column: s.column_name,
          column_type: (s.column_type ?? 'text') as ColumnDataType,
          operator: s.operator,
          params: Array.isArray(s.params)
            ? s.params.map((p: any) =>
                // Legacy format: {key: 'value', val: '45'} → '45'
                (p !== null && typeof p === 'object' && 'val' in p) ? String(p.val) : String(p)
              )
            : [],
          negate: s.negate === true,
          description: s.description,
          source_alias: s.source_alias ?? undefined,
        }),
      )
    : [],
});

const toRun = (r: any): RuleRun => ({
  id: String(r.id),
  rule_id: String(r.target_id),
  rule_name: r.target_name ?? '',
  pipeline_name: r.collection_type_name ?? undefined,
  triggered_by: (r.triggered_by ?? 'Manual') as RuleRun['triggered_by'],
  started_at: r.started_at ?? '',
  finished_at: r.finished_at ?? undefined,
  status: (r.status ?? 'Running') as RuleRun['status'],
  rows_in: Number(r.rows_in ?? 0),
  rows_matched: Number(r.rows_matched ?? 0),
  output_file: r.output_s3_key ? String(r.output_s3_key).split('/').pop() : undefined,
  output_size_bytes: r.output_size_bytes != null ? Number(r.output_size_bytes) : undefined,
  output_dataset_id: r.output_dataset_id != null ? String(r.output_dataset_id) : undefined,
  error_message: r.error_message ?? undefined,
});

const toSchedule = (r: any): MonitoringSchedule => ({
  id: String(r.id),
  name: r.name,
  rule_ids: Array.isArray(r.rule_ids) ? r.rule_ids.map(String) : [],
  mode: (r.mode ?? 'manual') as ScheduleMode,
  cron_expression: r.cron_expression ?? undefined,
  interval_minutes: r.interval_minutes ?? undefined,
  enabled: !!r.enabled,
  last_run_at: r.last_run_at ?? undefined,
  next_run_at: r.next_run_at ?? undefined,
});

const safeParse = (s: string): any => {
  try {
    return JSON.parse(s);
  } catch {
    return {};
  }
};

const toCollectionType = (r: any): CollectionType => ({
  id: String(r.id),
  name: r.name,
  description: r.description ?? '',
  icon_key: r.icon_key ?? 'custom',
  color_key: r.color_key ?? 'indigo',
  template_key: r.template_key ?? null,
  standard_schema: Array.isArray(r.standard_schema) ? r.standard_schema.map((c: any): StandardColumn => {
    if (c.data_type) {
      return {
        name: c.name ?? '', display_name: c.display_name ?? c.name ?? '',
        data_type: c.data_type as ColDataType,
        measurement_level: (c.measurement_level ?? 'nominal') as ColMeasurementLevel,
        cardinality_hint: (c.cardinality_hint ?? 'high_cardinality') as ColCardinalityHint,
        required: !!c.required, description: c.description,
      };
    }
    const st: string = c.semantic_type ?? '';
    let dt: ColDataType = 'text'; let ml: ColMeasurementLevel = 'nominal'; let ch: ColCardinalityHint = 'high_cardinality';
    if (st === 'numeric' || st === 'continuous') { dt = 'decimal'; ml = 'ratio'; ch = 'continuous'; }
    else if (st === 'categorical') { ch = 'low_cardinality'; }
    else if (st === 'date') { dt = 'date'; ml = 'interval'; }
    else if (st === 'percent') { dt = 'percentage'; ml = 'ratio'; ch = 'continuous'; }
    return { name: c.name ?? '', display_name: c.display_name ?? c.name ?? '', data_type: dt, measurement_level: ml, cardinality_hint: ch, required: !!c.required, description: c.description };
  }) : [],
  is_active: Number(r.is_active ?? 1),
  source_count: Number(r.source_count ?? 0),
  dataset_count: Number(r.dataset_count ?? 0),
  rule_count: Number(r.rule_count ?? 0),
  created_at: r.created_on ?? '',
  allowed_source_types: Array.isArray(r.allowed_source_types)
    ? r.allowed_source_types.map(String)
    : undefined,
});

const toColumnMapping = (r: any): ColumnMapping => ({
  id: String(r.id),
  dataset_id: String(r.dataset_id),
  standard_col_name: r.standard_col_name,
  file_col_name: r.file_col_name ?? null,
  mapped_by: (r.mapped_by ?? 'auto') as ColumnMapping['mapped_by'],
  confidence: r.confidence != null ? Number(r.confidence) : null,
});

const toCollectionTemplate = (r: any): CollectionTemplate => ({
  key: r.key,
  name: r.name,
  description: r.description ?? '',
  icon_key: r.icon_key ?? 'custom',
  color_key: r.color_key ?? 'indigo',
  columns: Array.isArray(r.columns) ? r.columns as StandardColumn[] : [],
});

// ─────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────

export const FileAnalyticsAPI = {
  // ---- Sources ----
  async listSources(collectionTypeId?: string): Promise<DataSource[]> {
    const params: Record<string, string> = {};
    if (collectionTypeId) params.collection_type_id = collectionTypeId;
    const { data } = await apiClient.get(`${BASE}/sources`, { params });
    return (data ?? []).map(toDataSource);
  },

  async createSource(payload: Partial<Pick<DataSource, 'name' | 'description' | 'icon_key' | 'color_key' | 'standard_schema' | 'allowed_source_types'>> & { name: string; collection_type_id?: string | number; source_type?: 'normal' | 'combined' | 'api' | 'graph'; api_endpoint?: string; api_credentials?: Record<string, any>; authenticator_id?: string | null; collection_mode?: string }): Promise<DataSource> {
    const body: Record<string, any> = { ...payload };
    if (payload.collection_type_id) body.collection_type_id = Number(payload.collection_type_id);
    const { data } = await apiClient.post(`${BASE}/sources`, body);
    return toDataSource(data);
  },

  async getGraphAuthorizeUrl(sourceId: number): Promise<string> {
    const { data } = await apiClient.get(`${BASE}/sources/graph/authorize`, { params: { source_id: sourceId } });
    return data.authorize_url as string;
  },

  async updateSource(sourceId: string, payload: Partial<Pick<DataSource, 'name' | 'description' | 'icon_key' | 'color_key' | 'standard_schema' | 'allowed_source_types' | 'api_params'>> & { name: string }): Promise<DataSource> {
    const { data } = await apiClient.put(`${BASE}/sources/${sourceId}`, payload);
    return toDataSource(data);
  },

  async deleteSource(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/sources/${id}`);
  },

  async resetSourceData(id: string): Promise<{ ok: boolean; datasets_deleted: number; rules_with_anomalies_cleared: number }> {
    const { data } = await apiClient.post(`${BASE}/sources/${id}/reset-data`);
    return data;
  },

  // ---- Authenticators ----
  async listAuthenticators(): Promise<import('./types').AuthenticatorRecord[]> {
    const { data } = await apiClient.get(`${BASE}/authenticators`);
    return data ?? [];
  },

  async createAuthenticator(payload: {
    name: string;
    login_url: string;
    token_path?: string;
    login_payload: import('./types').AuthPayloadRow[];
  }): Promise<import('./types').AuthenticatorRecord> {
    const { data } = await apiClient.post(`${BASE}/authenticators`, payload);
    return data;
  },

  async updateAuthenticator(id: string, payload: {
    name: string;
    login_url: string;
    token_path?: string;
    login_payload?: import('./types').AuthPayloadRow[];
  }): Promise<import('./types').AuthenticatorRecord> {
    const { data } = await apiClient.put(`${BASE}/authenticators/${id}`, payload);
    return data;
  },

  async testAuthenticator(id: string): Promise<import('./types').AuthenticatorTestResult> {
    const { data } = await apiClient.post(`${BASE}/authenticators/${id}/test`, {});
    return data;
  },

  async testAuthenticatorInline(payload: {
    login_url: string;
    token_path: string;
    login_payload: import('./types').AuthPayloadRow[];
  }): Promise<import('./types').AuthenticatorTestResult> {
    const { data } = await apiClient.post(`${BASE}/authenticators/test-inline`, payload);
    return data;
  },

  async deleteAuthenticator(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/authenticators/${id}`);
  },

  // ---- Datasets ----
  /** The original file exactly as it arrived. Returns a link when stored in S3,
   *  otherwise streams the bytes back directly. */
  async downloadOriginal(datasetId: number | string): Promise<{ url?: string }> {
    const { data } = await apiClient.get(`${BASE}/datasets/${datasetId}/download`);
    return data;
  },

  /** Past reads for a source that fetches its own data over the network.
   *  Such a source has no uploaded files, so its history lives elsewhere. */
  async fetchHistory(sourceId: number | string): Promise<{
    source: { id: number; name: string; source_type: string; api_endpoint: string | null };
    fetches: Array<{
      id: number; file_name: string; row_count: number | null; status: string;
      created_on: string | null; finished_on: string | null;
      collection_mode: string; is_current: boolean; error: string | null;
      can_view: boolean; can_download: boolean; can_delete: boolean;
    }>;
    fetch_count: number;
  }> {
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/fetch-history`);
    return data;
  },

  /** Every file loaded for a source, with its real storage size in S3. */
  async listSourceBatches(sourceId: number | string): Promise<{
    source: { id: number; name: string; source_type: string; collection_mode: string | null };
    batches: Array<{
      id: number; file_name: string; format: string; kind: string;
      row_count: number | null; status: string; in_scope: boolean;
      collection_mode: string; created_on: string;
      original_bytes: number; processed_bytes: number; total_bytes: number;
      can_view: boolean; can_download: boolean; reapplied_from: number | null;
    }>;
    batch_count: number; batch_bytes: number; layer_bytes: number;
    layers: Record<string, number>;
  }> {
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/batches`);
    return data;
  },

  /** Treat an earlier file as if it had just been uploaded. Everything loaded
   *  after it is undone by the ordinary comparison — no special rewind path. */
  async reapplyBatch(datasetId: number | string): Promise<{
    job_id: number; status: string; message?: string; already_running?: boolean;
  }> {
    const { data } = await apiClient.post(`${BASE}/datasets/${datasetId}/reapply`, {});
    return data;
  },

  /** Build the run list: files waiting plus every source that fetches its own data. */
  async assembleQueue(): Promise<{
    batch_id: string; operations: number;
    plan: Array<{ seq: number; source: string; how: string }>;
  }> {
    const { data } = await apiClient.post(`${BASE}/entity/assemble-queue`, {});
    return data;
  },

  /** Is Continuous Monitoring busy for this entity? Drives the on-screen lock. */
  async entityStatus(): Promise<{
    running: boolean; job_id?: number; phase?: string; source_name?: string | null;
    started_at?: string | null; locked_sources: number;
  }> {
    const { data } = await apiClient.get(`${BASE}/entity/status`);
    return data;
  },

  /**
   * Every detection rule with what it last found — one query, not one per
   * pipeline. This is the authority for the results screen; the live socket
   * feed carries the same figures sooner while a run is in progress.
   */
  async ruleBoard(): Promise<{ rules: RuleBoardRow[]; rule_count: number }> {
    const { data } = await apiClient.get(`${BASE}/entity/rule-board`);
    return data;
  },

  async listDatasets(params?: { source_id?: string; kind?: string; collection_type_id?: string }): Promise<DataSet[]> {
    const q: Record<string, string> = {};
    if (params?.source_id) q.source_id = params.source_id;
    if (params?.kind) q.kind = params.kind;
    if (params?.collection_type_id) q.collection_type_id = params.collection_type_id;
    const { data } = await apiClient.get(`${BASE}/datasets`, { params: q });
    return (data ?? []).map(toDataSet);
  },

  async getDataset(id: string): Promise<DataSet> {
    const { data } = await apiClient.get(`${BASE}/datasets/${id}`);
    return toDataSet(data);
  },

  async uploadDataset(
    sourceId: string,
    file: File,
    options?: {
      uploadMode?: UploadMode;
      requestAiReport?: boolean;
      sheetName?: string;
      onProgress?: (pct: number) => void;
    },
  ): Promise<DataSet> {
    const form = new FormData();
    form.append('file', file);
    form.append('source_id', sourceId);
    if (options?.uploadMode) form.append('upload_mode', options.uploadMode);
    if (options?.requestAiReport) form.append('request_ai_report', 'true');
    // Per-upload Excel sheet selection (Plan 109-02). Blank/undefined → backend defaults sheet 0.
    if (options?.sheetName) form.append('sheet_name', options.sheetName);
    const { data } = await apiClient.post(`${BASE}/datasets/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,
      onUploadProgress: (e) => {
        if (options?.onProgress && e.total)
          options.onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return toDataSet(data);
  },

  async deleteDataset(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/datasets/${id}`);
  },

  /**
   * REVERSIBLE descope / re-scope of a single batch (Plan 109-04, D3).
   * PATCH /datasets/{id}/scope { in_scope } flips fan_dataset.in_scope true OR
   * false — it NEVER touches is_active or the S3 parquet, so the batch stays
   * listed and recoverable. This is DISTINCT from deleteDataset (hard delete).
   * Returns the updated dataset so the caller can re-render the Descoped chip
   * in place without a full reload.
   */
  async setDatasetScope(id: number | string, inScope: boolean): Promise<DataSet> {
    const { data } = await apiClient.patch(`${BASE}/datasets/${id}/scope`, { in_scope: inScope });
    return toDataSet(data);
  },

  /**
   * Probe an Excel workbook's sheet names BEFORE the heavy upload (Plan 109-02).
   * Returns the ordered sheet names for .xlsx/.xls, or [] for non-Excel files
   * (so the caller can skip the sheet picker entirely).
   */
  async probeSheets(file: File): Promise<string[]> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post(`${BASE}/datasets/probe-sheets`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return Array.isArray(data?.sheets) ? data.sheets.map(String) : [];
  },

  async previewDataset(
    id: string,
    limit = 50,
    offset = 0,
    search?: string,
  ): Promise<{ columns: string[]; rows: any[][]; truncated: boolean; total: number }> {
    const params: Record<string, any> = { limit, offset };
    if (search && search.trim()) params.search = search.trim();
    const { data } = await apiClient.get(`${BASE}/datasets/${id}/preview`, { params });
    return data;
  },

  /**
   * Preview a source's CLEANED snapshot (saved_parquet_key) — works in the
   * consumed-snapshot state where no active raw batch exists. GET /sources/{id}/preview.
   */
  /** Preview the source's Bronze (Raw) layer — the parquet before cleaning runs. */
  async previewSourceBronze(
    sourceId: number | string,
    limit = 50,
    offset = 0,
    search?: string,
  ): Promise<{ columns: string[]; rows: any[][]; total: number; truncated: boolean }> {
    const params: Record<string, any> = { limit, offset };
    if (search) params.search = search;
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/preview-bronze`, { params });
    return data;
  },

  async previewSourceSnapshot(
    sourceId: number | string,
    limit = 100,
    offset = 0,
    search?: string,
  ): Promise<{ columns: string[]; rows: any[][]; truncated: boolean; total: number }> {
    const params: Record<string, any> = { limit, offset };
    if (search && search.trim()) params.search = search.trim();
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/preview`, { params });
    return data;
  },

  /**
   * Send a source's cleaned snapshot to Data Analytics (Data Collections) — creates
   * a da_collection + ingests the parquet in the background. POST /sources/{id}/register-as-collection.
   */
  async registerSourceAsCollection(
    sourceId: number | string,
    name?: string,
  ): Promise<{ status: string; data: { dc_id: string; status: string; title: string } }> {
    const { data } = await apiClient.post(
      `${BASE}/sources/${sourceId}/register-as-collection`,
      name ? { name } : {},
    );
    return data;
  },

  /**
   * Send a Gold rule run's matched-rows output to Data Analytics — creates a
   * da_collection + ingests the parquet in the background.
   * POST /runs/{runId}/send-to-analytics
   */
  async sendRunToAnalytics(
    runId: string | number,
    name?: string,
  ): Promise<{ status: string; data: { dc_id: string; status: string; title: string } }> {
    const { data } = await apiClient.post(
      `${BASE}/runs/${runId}/send-to-analytics`,
      name ? { name } : {},
    );
    return data;
  },

  // ---- Aggregations ----
  async listAggregations(collectionTypeId?: string, sourceId?: string | number): Promise<AggregationDefinition[]> {
    const params: Record<string, any> = {};
    if (collectionTypeId) params.collection_type_id = collectionTypeId;
    if (sourceId != null) params.source_id = sourceId;
    const { data } = await apiClient.get(`${BASE}/aggregations`, { params });
    return (data ?? []).map(toAggregation);
  },

  // CMR-RT — Ready aggregation output datasets the rule builder can target.
  async listAggregationTargets(collectionTypeId?: string): Promise<AggregationTarget[]> {
    const params: Record<string, any> = {};
    if (collectionTypeId) params.collection_type_id = collectionTypeId;
    const { data } = await apiClient.get(`${BASE}/aggregations/targets`, { params });
    return (data ?? []).map((t: any): AggregationTarget => ({
      output_dataset_id: Number(t.output_dataset_id),
      agg_id: Number(t.agg_id),
      name: String(t.name ?? ''),
      columns: Array.isArray(t.columns)
        ? t.columns.map((c: any) => ({ name: String(c.name), semantic_type: c.semantic_type ?? null }))
        : [],
    }));
  },

  async createAggregation(payload: {
    name: string;
    source_dataset_id?: string;
    data_source_id?: number;
    type: AggregationType;
    spec: Record<string, any>; // may include measure_aggregations: string[]
    sources?: RuleSource[];
    run_now?: boolean;
  }): Promise<AggregationDefinition> {
    const { data } = await apiClient.post(`${BASE}/aggregations`, {
      ...payload,
      source_dataset_id: payload.source_dataset_id ? Number(payload.source_dataset_id) : undefined,
    }, { timeout: 300_000 });
    return toAggregation(data);
  },

  async updateAggregation(id: string, payload: {
    name: string;
    source_dataset_id?: string;
    data_source_id?: number;
    type: AggregationType;
    spec: Record<string, any>;
    sources?: RuleSource[];
    run_now?: boolean;
  }): Promise<AggregationDefinition> {
    const { data } = await apiClient.put(`${BASE}/aggregations/${id}`, {
      ...payload,
      source_dataset_id: payload.source_dataset_id ? Number(payload.source_dataset_id) : undefined,
    }, { timeout: 300_000 });
    return toAggregation(data);
  },

  async runAggregation(
    id: string,
    datasetId?: string,
    datasetIds?: number[],
  ): Promise<{ output_dataset_id?: string }> {
    // `datasetIds` (Plan 109, D2 selection) goes in the JSON body the run endpoint
    // accepts (Plan 03 wiring); the legacy single `dataset_id` query param is kept
    // for the one-batch override. Both are optional → fully backward compatible.
    const body = datasetIds && datasetIds.length ? { dataset_ids: datasetIds } : {};
    const { data } = await apiClient.post(`${BASE}/aggregations/${id}/run`, body, {
      params: datasetId ? { dataset_id: Number(datasetId) } : undefined,
      timeout: 300_000,
    });
    return { output_dataset_id: data?.output_dataset_id != null ? String(data.output_dataset_id) : undefined };
  },

  async deleteAggregation(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/aggregations/${id}`);
  },

  // ---- Rules ----
  async listRules(collectionTypeId?: string): Promise<Rule[]> {
    const params: Record<string, string> = {};
    if (collectionTypeId) params.collection_type_id = collectionTypeId;
    const { data } = await apiClient.get(`${BASE}/rules`, { params });
    return (data ?? []).map(toRule);
  },

  // The rsk endpoints live outside the FAN namespace — use the absolute path.
  async listRisks(): Promise<RiskOption[]> {
    const { data } = await apiClient.get('/api/v1/rsk/risks', { params: { limit: 200 } });
    return (data?.data ?? []).map((r: any) => ({
      id: Number(r.id),
      risk_code: r.risk_code,
      risk_name: r.risk_name,
    }));
  },

  async createRule(payload: {
    name: string;
    description?: string;
    severity: Rule['severity'];
    tags: string[];
    activate: boolean;
    collection_type_id?: number;
    data_source_id?: number;
    agg_target_dataset_id?: number;
    risk_id?: number | null;
    sources?: RuleSource[];
    steps: Array<{
      target_dataset_id: string | null;
      column_name: string;
      column_type: ColumnDataType;
      operator: string;
      params: string[];
      description?: string;
      source_alias?: string;
    }>;
  }): Promise<Rule> {
    const body = {
      ...payload,
      risk_id: payload.risk_id ?? null,
      steps: payload.steps.map(s => ({ ...s, target_dataset_id: s.target_dataset_id ? Number(s.target_dataset_id) : null })),
    };
    const { data } = await apiClient.post(`${BASE}/rules`, body);
    return toRule(data);
  },

  async updateRule(id: string, payload: {
    name: string;
    description?: string;
    severity: Rule['severity'];
    tags: string[];
    activate: boolean;
    collection_type_id?: number;
    data_source_id?: number;
    agg_target_dataset_id?: number;
    risk_id?: number | null;
    sources?: RuleSource[];
    steps: Array<{
      target_dataset_id: string | null;
      column_name: string;
      column_type: ColumnDataType;
      operator: string;
      params: string[];
      description?: string;
      source_alias?: string;
    }>;
  }): Promise<Rule> {
    const body = {
      ...payload,
      risk_id: payload.risk_id ?? null,
      steps: payload.steps.map(s => ({ ...s, target_dataset_id: s.target_dataset_id ? Number(s.target_dataset_id) : null })),
    };
    const { data } = await apiClient.put(`${BASE}/rules/${id}`, body);
    return toRule(data);
  },

  async runRule(
    id: string,
    datasetId?: string,
    datasetIds?: number[],
  ): Promise<{ run_id: string; rows_matched: number; output_dataset_id?: string }> {
    // `datasetIds` (Plan 109, D2 selection) → JSON body `dataset_ids` the run
    // endpoint accepts (Plan 03 wiring); legacy single `dataset_id` query param
    // kept for the one-batch override. Both optional → backward compatible.
    const body = datasetIds && datasetIds.length ? { dataset_ids: datasetIds } : {};
    const { data } = await apiClient.post(`${BASE}/rules/${id}/run`, body, {
      params: datasetId ? { dataset_id: Number(datasetId) } : undefined,
      timeout: 300_000,
    });
    return data;
  },

  async runAllActive(collectionTypeId?: string): Promise<{ triggered: any[]; failed: any[] }> {
    const { data } = await apiClient.post(
      `${BASE}/rules/run-all-active`,
      {},
      { params: collectionTypeId ? { collection_type_id: collectionTypeId } : undefined, timeout: 600_000 },
    );
    return data;
  },

  async deleteRule(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/rules/${id}`);
  },

  // ---- Anomalies (triage — R17/R18) ----
  // Authoritative distinct-rows-flagged for the Runs summary — backend dedups
  // record_key over ACTIVE (latest-run-scoped) anomalies, no client-side 500 cap.
  async anomalyFlaggedSummary(collectionTypeId?: string | number): Promise<{ distinct_rows: number; total_findings: number }> {
    const params: Record<string, any> = {};
    if (collectionTypeId != null) params.collection_type_id = Number(collectionTypeId);
    const { data } = await apiClient.get(`${BASE}/anomalies/flagged-summary`, { params });
    return { distinct_rows: data?.distinct_rows ?? 0, total_findings: data?.total_findings ?? 0 };
  },

  async listAnomalies(filters?: {
    status?: string;
    rule_id?: number;
    risk_id?: number;
    collection_type_id?: number;
  }): Promise<Anomaly[]> {
    const { data } = await apiClient.get(`${BASE}/anomalies`, {
      params: { ...filters, limit: 500 },
    });
    return data ?? [];
  },

  async triageAnomaly(
    id: number,
    body: { status?: string; assignee_user_id?: number | null; notes?: string },
  ): Promise<Anomaly> {
    const { data } = await apiClient.patch(`${BASE}/anomalies/${id}`, body);
    return data;
  },

  async triageAnomalyDecision(anomalyId: number, payload: {
    decision: 'trivial' | 'escalate' | 'task';
    reporter_feedback?: string;
    risk_severity?: string;
    capa_owner_id?: number;
    instructions?: string;
    target_date?: string;
    task_title?: string;
    task_description?: string;
    task_assignee_id?: number;
    task_approver_id?: number | null;
    task_validator_id?: number | null;
    task_due_date?: string;
    task_grace_period_days?: number;
    task_priority?: string;
  }): Promise<Anomaly> {
    const { data } = await apiClient.patch(`${BASE}/anomalies/${anomalyId}/triage`, payload);
    return data;
  },

  // ---- Runs ----
  async listRuns(limit = 50, collectionTypeId?: string): Promise<RuleRun[]> {
    const params: Record<string, any> = { limit, target_kind: 'rule' };
    if (collectionTypeId) params.collection_type_id = collectionTypeId;
    const { data } = await apiClient.get(`${BASE}/runs`, { params });
    return (data ?? []).map(toRun);
  },

  async downloadRun(id: string): Promise<string> {
    const { data } = await apiClient.get(`${BASE}/runs/${id}/download`);
    return data.url;
  },

  /** Preview matched rows directly from a run's output parquet — works even when output_dataset_id is null. */
  async previewRun(
    runId: string | number,
    limit = 50,
    offset = 0,
    search?: string,
  ): Promise<{ columns: string[]; rows: any[][]; total: number; truncated: boolean }> {
    const params: Record<string, any> = { limit, offset };
    if (search) params.search = search;
    const { data } = await apiClient.get(`${BASE}/runs/${runId}/preview`, { params });
    return data;
  },

  /** List rule runs that have a Gold output parquet — used by the "Read from result set" picker. */
  async listRunsWithOutput(limit = 200): Promise<RunWithOutput[]> {
    const { data } = await apiClient.get(`${BASE}/runs/with-output`, { params: { limit } });
    return data ?? [];
  },

  /** Promote a Gold run parquet to Bronze and enqueue the Silver ingest job — no file upload needed. */
  async ingestFromRun(
    sourceId: string | number,
    runId: string | number,
    collectionMode: 'replace' | 'append' = 'replace',
  ): Promise<{ job_id: number; status: string }> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/ingest-from-run`, {
      run_id: runId,
      collection_mode: collectionMode,
    });
    return data;
  },

  async cloneSource(
    sourceId: string | number,
    newName: string,
  ): Promise<DataSource> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/clone`, { name: newName });
    return toDataSource(data);
  },

  // ---- Metrics (run + anomaly aggregates — R19) ----
  async listMetrics(): Promise<{
    total_runs: number;
    total_anomalies: number;
    open_anomalies: number;
    active_rules: number;
    by_pipeline: Array<{
      collection_type_id: number;
      pipeline_name: string;
      runs: number;
      anomalies: number;
    }>;
    by_rule?: Array<{ rule_id: number; rule_name: string; anomalies: number }>;
    by_status: Array<{ status: string; count: number }>;
    rules_by_status: Array<{ label: string; count: number }>;
    rules_by_severity: Array<{ label: string; count: number }>;
    runs_by_status: Array<{ label: string; count: number }>;
  }> {
    const { data } = await apiClient.get(`${BASE}/metrics`);
    return data;
  },

  // ---- Schedules ----
  async listSchedules(): Promise<MonitoringSchedule[]> {
    const { data } = await apiClient.get(`${BASE}/schedules`);
    return (data ?? []).map(toSchedule);
  },

  async createSchedule(payload: {
    name: string;
    rule_ids: string[];
    mode: ScheduleMode;
    cron_expression?: string;
    interval_minutes?: number;
    enabled: boolean;
  }): Promise<MonitoringSchedule> {
    const body = { ...payload, rule_ids: payload.rule_ids.map(id => Number(id)) };
    const { data } = await apiClient.post(`${BASE}/schedules`, body);
    return toSchedule(data);
  },

  async deleteSchedule(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/schedules/${id}`);
  },

  // ---- Collection Types ----
  async listCollectionTypes(): Promise<CollectionType[]> {
    const { data } = await apiClient.get(`${BASE}/collection-types`);
    return (data ?? []).map(toCollectionType);
  },

  async getCollectionTypeTemplates(): Promise<CollectionTemplate[]> {
    const { data } = await apiClient.get(`${BASE}/collection-types/templates`);
    return (data ?? []).map(toCollectionTemplate);
  },

  async createCollectionType(payload: {
    name: string;
    description?: string;
    template_key?: string;
    icon_key?: string;
    color_key?: string;
  }): Promise<CollectionType> {
    const { data } = await apiClient.post(`${BASE}/collection-types`, payload);
    return toCollectionType(data);
  },

  async updateCollectionType(id: string, payload: {
    name?: string;
    description?: string;
    icon_key?: string;
    color_key?: string;
    standard_schema?: StandardColumn[];
    allowed_source_types?: string[];
  }): Promise<CollectionType> {
    const { data } = await apiClient.put(`${BASE}/collection-types/${id}`, payload);
    return toCollectionType(data);
  },

  async deleteCollectionType(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/collection-types/${id}`);
  },

  // ---- Pipeline ↔ data-source links (multi-source pipelines) ----
  async getPipelineDataSources(pipelineId: string): Promise<DataSource[]> {
    const { data } = await apiClient.get(`${BASE}/pipelines/${pipelineId}/data-sources`);
    return (data ?? []).map(toDataSource);
  },

  async setPipelineDataSources(pipelineId: string, dataSourceIds: string[]): Promise<void> {
    await apiClient.put(`${BASE}/pipelines/${pipelineId}/data-sources`, {
      data_source_ids: dataSourceIds.map(Number),
    });
  },

  // ---- Column Mappings ----
  async getColumnMappings(datasetId: string): Promise<ColumnMapping[]> {
    const { data } = await apiClient.get(`${BASE}/datasets/${datasetId}/mappings`);
    return (data ?? []).map(toColumnMapping);
  },

  async autoMapDataset(datasetId: string): Promise<ColumnMapping[]> {
    const { data } = await apiClient.post(`${BASE}/datasets/${datasetId}/auto-map`, {});
    // Backend returns { mapped, total, mappings: [...] }
    const arr = Array.isArray(data) ? data : (data?.mappings ?? []);
    return arr.map(toColumnMapping);
  },

  async saveColumnMappings(
    datasetId: string,
    mappings: Array<{ standard_col_name: string; file_col_name: string | null }>,
  ): Promise<void> {
    await apiClient.post(`${BASE}/datasets/${datasetId}/mappings`, { mappings });
  },

  // Contract-named aliases (Phase-114 verified column-mapping contract).
  // GET …/mappings → pre-seeded fuzzy suggestions (file_col_name may be null = unmatched).
  async getDatasetMappings(datasetId: string): Promise<ColumnMapping[]> {
    return this.getColumnMappings(datasetId);
  },

  // POST …/mappings → 400 (with detail) if any REQUIRED column is unmapped (nothing
  // persisted); 200 {ok:true} rewrites the parquet to defined names and flips → Ready.
  async saveDatasetMappings(
    datasetId: string,
    mappings: Array<{ standard_col_name: string; file_col_name: string | null }>,
  ): Promise<void> {
    return this.saveColumnMappings(datasetId, mappings);
  },

  // ---- Clean Recipes (pipeline-scoped — legacy) ----
  async getCleanRecipe(pipelineId: string): Promise<CleanRecipe | null> {
    const { data } = await apiClient.get(`${BASE}/pipelines/${pipelineId}/clean-recipe`);
    if (!data || data.recipe === null) return null;
    return toCleanRecipe(data);
  },

  async saveCleanRecipe(
    pipelineId: string,
    recipe: { name?: string; steps: Array<{ operation: CleanStep['operation']; params: Record<string, any> }> },
  ): Promise<CleanRecipe> {
    const { data } = await apiClient.post(`${BASE}/pipelines/${pipelineId}/clean-recipe`, recipe);
    return toCleanRecipe(data);
  },

  // ---- Clean Recipes (data-source-scoped — new architecture) ----
  async getSourceCleanRecipe(sourceId: string): Promise<CleanRecipe | null> {
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/clean-recipe`);
    if (!data || ('recipe' in data && data.recipe === null)) return null;
    return toCleanRecipe(data as any);
  },

  async saveSourceCleanRecipe(
    sourceId: string,
    recipe: { name: string; steps: Array<{ operation: CleanStep['operation']; params: Record<string, any> }> },
  ): Promise<CleanRecipe> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/clean-recipe`, recipe);
    return toCleanRecipe(data);
  },

  // ---- Apply Clean ----
  async applyClean(datasetId: string): Promise<DataSet> {
    const { data } = await apiClient.post(
      `${BASE}/datasets/${datasetId}/apply-clean`,
      {},
      { timeout: 300_000 },
    );
    return toDataSet(data);
  },

  /**
   * Orchestrated pipeline-run cleaning (Plan 109-04, D6): applies EVERY
   * constituent source's clean recipe across the selected dataset set in ONE
   * call ("3 sources × 5 steps = 15 applied"). `selection` is keyed by
   * stringified data_source_id → the batch ids to clean for that source;
   * absent/empty per source falls back to all in-scope batches (D2 default).
   */
  async runPipelineClean(
    pipelineId: string,
    selection?: Record<string, number[]>,
  ): Promise<RunCleanResult> {
    const body = selection && Object.keys(selection).length ? { selection } : {};
    const { data } = await apiClient.post(
      `${BASE}/pipelines/${pipelineId}/run-clean`,
      body,
      { timeout: 600_000 },
    );
    return data as RunCleanResult;
  },

  // ---- AI Report ----
  async getAiReport(datasetId: string): Promise<AiReport> {
    const { data } = await apiClient.get(`${BASE}/datasets/${datasetId}/ai-report`);
    return toAiReport(data);
  },

  // ── Phase-114 Continuous Monitoring Redesign methods ─────────────────────

  /**
   * Run a data source (clean + enrich + save).  POST /sources/{id}/run
   * mode='replace' wipes the saved copy first; mode='append' merges new rows.
   * datasetIds optionally pins which raw batches to include (BR-3/BR-4).
   */
  async runDataSource(
    sourceId: number,
    mode: 'replace' | 'append',
    datasetIds?: number[],
    dynamicParams?: Record<string, string>,
  ): Promise<{ saved_parquet_key: string; saved_row_count: number }> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/run`, {
      mode,
      dataset_ids: datasetIds,
      ...(dynamicParams && Object.keys(dynamicParams).length > 0 && { dynamic_params: dynamicParams }),
    });
    return data;
  },

  /**
   * Enqueue the Celery Silver ingest pipeline for a source that already has
   * a bronze layer (i.e. a file was uploaded via the normal upload flow).
   * POST /sources/{sourceId}/run-ingest → {job_id, status:'queued'}
   * Poll getJobStatus until status === 'done' | 'failed'.
   */
  async ingestDataset(sourceId: number | string): Promise<{ job_id: number; status: string }> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/run-ingest`, {});
    return { job_id: Number(data.job_id), status: String(data.status ?? 'queued') };
  },

  /**
   * Poll a fan_job row by id.
   * GET /jobs/{jobId} → {id, status, progress, error_message, ...}
   */
  async getJobStatus(jobId: number): Promise<{
    id: number;
    status: 'queued' | 'running' | 'done' | 'failed';
    progress: Record<string, any> | null;
    error_message: string | null;
    started_at: string | null;
    finished_at: string | null;
  }> {
    const { data } = await apiClient.get(`${BASE}/jobs/${jobId}`);
    return {
      id: Number(data.id),
      status: (data.status ?? 'queued') as 'queued' | 'running' | 'done' | 'failed',
      progress: data.progress ?? null,
      error_message: data.error_message ?? null,
      started_at: data.started_at ?? null,
      finished_at: data.finished_at ?? null,
    };
  },

  /**
   * Run a pipeline = refresh all sources, then run all active rules.
   * POST /collection-types/{id}/run (orchestrated endpoint)
   *
   * dynamicParamsBySource: keyed by source_id (string), value = {param_key: value}.
   * Required when any source has dynamic params — backend returns 422 listing what's needed.
   */
  async runPipeline(
    pipelineId: number,
    dynamicParamsBySource?: Record<string, Record<string, string>>,
  ): Promise<{ rules_run: number; anomalies_found: number }> {
    const body: Record<string, any> = {};
    if (dynamicParamsBySource && Object.keys(dynamicParamsBySource).length > 0) {
      body.dynamic_params_by_source = dynamicParamsBySource;
    }
    const { data } = await apiClient.post(
      `${BASE}/collection-types/${pipelineId}/run`,
      body,
      { timeout: 600_000 },
    );
    const rules = (data?.rules ?? []) as Array<{ rows_matched?: number; status: string }>;
    return {
      rules_run: rules.filter(r => r.status === 'ok').length,
      anomalies_found: rules.reduce((s, r) => s + (r.rows_matched ?? 0), 0),
    };
  },

  /**
   * List all Enrich/Combine links for a data source.  GET /sources/{id}/links
   */
  async listSourceLinks(sourceId: number): Promise<DataSourceLink[]> {
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/links`);
    return (data ?? []) as DataSourceLink[];
  },

  /**
   * Create an Enrich or Combine link for a data source.  POST /sources/{id}/links
   */
  async createSourceLink(sourceId: number, link: DataSourceLinkCreate): Promise<DataSourceLink> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/links`, link);
    return data as DataSourceLink;
  },

  /**
   * Delete an Enrich/Combine link.  DELETE /sources/{id}/links/{linkId}
   */
  async deleteSourceLink(sourceId: number, linkId: number): Promise<void> {
    await apiClient.delete(`${BASE}/sources/${sourceId}/links/${linkId}`);
  },

  // ---- Source Rules (unified clean | formula | lookup) ----
  async listSourceRules(sourceId: number): Promise<SourceRule[]> {
    const { data } = await apiClient.get(`${BASE}/sources/${sourceId}/rules`);
    return (data ?? []) as SourceRule[];
  },

  /**
   * Every preparation rule for the entity, in the order they run.
   *
   * The rules are written against a data set but ordered across all of them, so
   * this is the reading that shows what actually happens first. Each rule comes
   * back with its data set's name, so the list can be grouped for reading
   * without losing the single order underneath.
   */
  async listPreparationRules(): Promise<SourceRule[]> {
    const { data } = await apiClient.get(`${BASE}/preparation-rules`);
    return (data ?? []) as SourceRule[];
  },

  async createSourceRule(sourceId: number, rule: SourceRuleCreate): Promise<SourceRule> {
    const { data } = await apiClient.post(`${BASE}/sources/${sourceId}/rules`, rule);
    return data as SourceRule;
  },

  async updateSourceRule(sourceId: number, ruleId: number, patch: SourceRuleUpdate): Promise<SourceRule> {
    const { data } = await apiClient.patch(`${BASE}/sources/${sourceId}/rules/${ruleId}`, patch);
    return data as SourceRule;
  },

  async deleteSourceRule(sourceId: number, ruleId: number): Promise<void> {
    await apiClient.delete(`${BASE}/sources/${sourceId}/rules/${ruleId}`);
  },

  async reorderSourceRules(sourceId: number, rules: { id: number; step_order: number }[]): Promise<void> {
    await apiClient.put(`${BASE}/sources/${sourceId}/rules/reorder`, { rules });
  },

  /**
   * Get pipeline dependency readiness.  GET /collection-types/{id}/dependencies
   * Returns source_state and any blocking_sources so the UI can show Blocked vs Ready.
   */
  async getPipelineDependencies(pipelineId: number): Promise<PipelineDependencyStatus> {
    const { data } = await apiClient.get(`${BASE}/collection-types/${pipelineId}/dependencies`);
    return data as PipelineDependencyStatus;
  },
};

export default FileAnalyticsAPI;
